import axios from 'axios'
import getConfig from 'next/config'
import { useEffect, useState } from 'react'
import { getResponseData, isIndia } from 'utils/commonUtils'

const {
  publicRuntimeConfig: { adminPortalUrl },
} = getConfig()

export interface IProvince {
  id: number
  name: string
}

// Session-persistent cache for locations data
interface CacheEntry {
  data: any[]
  timestamp: number
}

const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes in milliseconds
const CACHE_KEY_PREFIX = 'locations_cache_level_'

// Helper functions for sessionStorage cache
const getCacheKey = (level: number) => `${CACHE_KEY_PREFIX}${level}`

const getCachedData = (level: number): CacheEntry | null => {
  try {
    const cached = sessionStorage.getItem(getCacheKey(level))
    return cached ? JSON.parse(cached) : null
  } catch {
    return null
  }
}

const setCachedData = (level: number, entry: CacheEntry) => {
  try {
    sessionStorage.setItem(getCacheKey(level), JSON.stringify(entry))
  } catch {
    // Ignore storage errors
  }
}

const useGetLocations = (level = 2) => {
  const [locations, setLocations] = useState<any[]>([])
  const [isFinish, setIsFinish] = useState<boolean>(false)

  useEffect(() => {
    let isMounted = true

    const getLocations = async () => {
      try {
        // Check cache first
        const cachedEntry = getCachedData(level)
        const now = Date.now()

        if (cachedEntry && now - cachedEntry.timestamp < CACHE_DURATION) {
          // Use cached data if it's still valid
          setLocations(cachedEntry.data)
          setIsFinish(true)
          return
        }

        // Fetch from API if not cached or cache expired
        const response = await axios.get(
          `${adminPortalUrl}/v1/location-info/list/level?level=${level}`
        )
        const data = getResponseData<any[]>(response)
        const nextData = isIndia
          ? data?.map((i) => {
              return { ...i, locationId: i?.locationId.toString() }
            })
          : data

        // Cache the processed data
        setCachedData(level, {
          data: nextData || [],
          timestamp: now,
        })

        if (isMounted) {
          setLocations(nextData || [])
        }
      } catch (error) {
        if (isMounted) {
          setLocations([])
        }
      } finally {
        if (isMounted) {
          setIsFinish(true)
        }
      }
    }

    getLocations()

    return () => {
      isMounted = false
    }
  }, [level])

  return { locations, isFinish }
}

export default useGetLocations
